import {
  type FeatureFlags,
  type ReturnTypeFromGetPersonalSettings,
  type ReturnTypeFromGetUser,
  type UsePermissionsReturn,
  useFeatureFlags,
  usePermissions,
  usePersonalSettings,
  useUser,
} from '@tyro/api';
import { useTranslation } from '@tyro/i18n';
import {
  type MenuLink,
  type NavCategory,
  NavObjectType,
  type NonMenuLink,
  type RootGroup,
  type RootLink,
} from '@tyro/router/types';
import { type JSX, useEffect, useMemo, useState } from 'react';
import { getNavCategories } from '../router';

export interface NavigationMenuLink {
  title: string;
  path?: string;
  icon?: never;
  info?: JSX.Element;
  children?: Omit<NavigationMenuLink, 'children'>[];
}

export interface NavigationRootGroup {
  title: string;
  path?: string;
  icon: JSX.Element;
  info?: JSX.Element;
  children?: NavigationMenuLink[];
}

export type NavigationConfig = Array<{
  subheader: string;
  children: NavigationRootGroup[];
}>;

// Create a function to join url paths together and remove any double slashes
// that may be created.
const joinPaths = (...paths: string[]) =>
  paths.join('/').replace(/\/+/g, '/').replace(/\/$/, '');

function filterAndMapToNavigationMenuLink(
  navItems: (MenuLink | NonMenuLink)[],
  permissions: UsePermissionsReturn,
  featureFlags: FeatureFlags,
  user: ReturnTypeFromGetUser,
  personalSettings: ReturnTypeFromGetPersonalSettings,
  parentPaths: string[],
): NavigationMenuLink[] {
  return navItems.reduce((acc, navItem) => {
    const hasAccess =
      typeof navItem.hasAccess === 'function'
        ? navItem.hasAccess({
            permissions,
            featureFlags,
            ...user,
            personalSettings,
          })
        : true;

    if (!hasAccess) return acc;

    if (navItem.type === NavObjectType.MenuLink) {
      const { title, path, info, children } = navItem;

      if (children) {
        const childPaths = path ? [...parentPaths, path] : parentPaths;

        const filteredChildren = filterAndMapToNavigationMenuLink(
          children,
          permissions,
          featureFlags,
          user,
          personalSettings,
          childPaths,
        );

        if (filteredChildren.length > 0) {
          acc.push({ title, children: filteredChildren });
        } else if (path) {
          const fullPath = joinPaths(...parentPaths, path);
          acc.push({ title, info, path: fullPath });
        }
      } else if (path) {
        const fullPath = joinPaths(...parentPaths, path);
        acc.push({ title, info, path: fullPath });
      }
    }

    return acc;
  }, [] as NavigationMenuLink[]);
}

function filterAndMapToNavigationRootGroup(
  navItems: (RootLink | RootGroup | NonMenuLink)[],
  permissions: UsePermissionsReturn,
  featureFlags: FeatureFlags,
  user: ReturnTypeFromGetUser,
  personalSettings: ReturnTypeFromGetPersonalSettings,
): NavigationRootGroup[] {
  return navItems.reduce((acc, navItem) => {
    const hasAccess =
      typeof navItem.hasAccess === 'function'
        ? navItem.hasAccess({
            permissions,
            featureFlags,
            ...user,
            personalSettings,
          })
        : true;

    if (!hasAccess) return acc;

    if (navItem.type === NavObjectType.RootGroup && navItem.children) {
      const { title, icon, info, children, path } = navItem;
      const parentPaths = path ? [path] : [];

      const filteredChildren = filterAndMapToNavigationMenuLink(
        children,
        permissions,
        featureFlags,
        user,
        personalSettings,
        parentPaths,
      );

      if (filteredChildren.length > 0) {
        acc.push({ title, icon, info, path, children: filteredChildren });
      }
    } else if (navItem.type === NavObjectType.RootLink) {
      const { title, path, icon, info } = navItem;
      acc.push({ title, icon, info, path });
    }

    return acc;
  }, [] as NavigationRootGroup[]);
}

export function useNavigationConfig() {
  const { t } = useTranslation(['navigation']);
  const permissions = usePermissions();
  const featureFlags = useFeatureFlags();
  const { user, activeProfile } = useUser();
  const { isLoading, ...personalSettings } = usePersonalSettings();
  const [navCategories, setNavCategories] = useState<NavCategory[]>([]);

  useEffect(() => {
    let ignore = false;
    let retryTimeout: ReturnType<typeof setTimeout>;

    getNavCategories(t)
      .then((categories) => {
        if (!ignore) setNavCategories(categories);
      })
      .catch(() => {
        // Retry once if initial load fails (e.g. dev server cold start)
        retryTimeout = setTimeout(() => {
          getNavCategories(t).then((categories) => {
            if (!ignore) setNavCategories(categories);
          });
        }, 2000);
      });

    return () => {
      ignore = true;
      clearTimeout(retryTimeout);
    };
  }, [t]);

  return useMemo(() => {
    if (navCategories.length === 0) return [];

    const getUserObject = {
      user,
      activeProfile,
    } as ReturnTypeFromGetUser;

    // 1. Combine the root groups under the same subheader
    const combinedNavCategories = navCategories.reduce(
      (acc, navCategory) => {
        if (
          typeof navCategory.hasAccess === 'function' &&
          !navCategory.hasAccess({
            permissions,
            featureFlags,
            ...getUserObject,
            personalSettings:
              personalSettings as ReturnTypeFromGetPersonalSettings,
          })
        ) {
          return acc;
        }

        const subheader = navCategory.title;
        const rootGroups = navCategory.children;

        const existingSubheader = acc.find(
          ({ subheader: usedSubheader }) => usedSubheader === subheader,
        );

        if (existingSubheader) {
          existingSubheader.children.push(...rootGroups);
        } else {
          acc.push({ subheader, children: [...rootGroups] });
        }

        return acc;
      },
      [] as {
        subheader: string;
        children: (RootLink | RootGroup | NonMenuLink)[];
      }[],
    );

    return combinedNavCategories.reduce((acc, { subheader, children }) => {
      const filteredChildren = filterAndMapToNavigationRootGroup(
        children,
        permissions,
        featureFlags,
        getUserObject,
        personalSettings as ReturnTypeFromGetPersonalSettings,
      );

      if (filteredChildren.length > 0) {
        acc.push({ subheader, children: filteredChildren });
      }

      return acc;
    }, [] as NavigationConfig);
  }, [navCategories, permissions, t, featureFlags, user, activeProfile]);
}
